/**
 * ProxyUploadService.js
 * Handles chunk uploads through backend proxy to avoid CORS issues
 */

import { getAccessTokenSilentlyOutsideComponent } from "./workspace.service";
import criticalLoggingService from "./CriticalLoggingService";
import { openDB } from "idb";

export class ProxyUploadService {
  constructor(
    audioFileId,
    apiUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api",
  ) {
    console.log(
      "🚀 [ProxyUpload] NEW VERSION WITH INDEXEDDB + SINGLE CHUNK SUPPORT INITIALIZED!",
    );
    this.audioFileId = audioFileId;
    this.apiUrl = apiUrl;
    // Content-Type used to label chunk uploads. Defaults to WebM/Opus but is
    // overridden via setContentType() with the recorder's actual MIME type so
    // the label matches the real bytes (e.g. Safari records audio/mp4).
    this.contentType = "audio/webm;codecs=opus";
    this.uploadedBytes = 0;
    this.totalBytes = 0;
    this.chunks = [];
    this.chunkIndex = 0;
    this.isUploading = false;
    // GCS requires minimum 256KB chunks (except final chunk)
    this.chunkSize = 256 * 1024; // 256KB chunks for GCS compatibility
    this.buffer = []; // Buffer to accumulate small chunks
    this.bufferSize = 0;
    this.isFinalChunk = false; // Track if we've flushed the final chunk
    this.immediateUpload = true; // Upload chunks immediately when available
    this.db = null;
    this.sessionId = `session-${audioFileId}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    console.log(`🔑 [ProxyUpload] Session ID: ${this.sessionId}`);
    this.dbInitPromise = null;
  }

  /**
   * Initialize IndexedDB for persisting chunks
   * Can be called multiple times - will only initialize once
   */
  async initDB() {
    // Return existing promise if initialization is in progress
    if (this.dbInitPromise) {
      return this.dbInitPromise;
    }

    // Return early if already initialized
    if (this.db) {
      return;
    }

    // Create and store the initialization promise
    this.dbInitPromise = (async () => {
      try {
        this.db = await openDB("AudioScribeRecording", 2, {
          upgrade(db, oldVersion, _newVersion, transaction) {
            if (oldVersion < 1) {
              if (!db.objectStoreNames.contains("chunks")) {
                const store = db.createObjectStore("chunks", {
                  keyPath: "id",
                  autoIncrement: true,
                });
                store.createIndex("audioFileId", "audioFileId", {
                  unique: false,
                });
                store.createIndex("timestamp", "timestamp", { unique: false });
                store.createIndex("sessionId", "sessionId", { unique: false });
              }
              if (!db.objectStoreNames.contains("metadata")) {
                db.createObjectStore("metadata", {
                  keyPath: "audioFileId",
                });
              }
            }

            if (oldVersion < 2) {
              // Add new indexes for better cleanup. Use the upgrade transaction
              // that idb passes in — creating a NEW db.transaction() inside the
              // upgradeneeded handler throws "A version change transaction is
              // running", aborting the entire upgrade. That left IndexedDB
              // uninitialized and silently broke real-time recording chunk
              // buffering for any fresh/cleared browser (0 chunks uploaded).
              if (db.objectStoreNames.contains("chunks")) {
                const store = transaction.objectStore("chunks");
                if (!store.indexNames.contains("sessionId")) {
                  store.createIndex("sessionId", "sessionId", {
                    unique: false,
                  });
                }
              }
            }
          },
        });
        console.log("[ProxyUpload] IndexedDB initialized");

        // Check for any unfinished uploads from previous sessions
        await this.recoverFromIndexedDB();
      } catch (error) {
        console.error("[ProxyUpload] Failed to initialize IndexedDB:", error);
        this.dbInitPromise = null; // Reset promise on error so it can be retried
        throw error;
      }
    })();

    return this.dbInitPromise;
  }

  /**
   * Recover any pending chunks from IndexedDB
   */
  async recoverFromIndexedDB() {
    if (!this.db) return;

    try {
      await this.cleanupStaleData();

      const metadata = await this.db.get("metadata", this.audioFileId);
      const shouldContinue = await this.processMetadata(metadata);
      if (!shouldContinue) return;

      const chunks = await this.getStoredChunks();
      if (chunks.length === 0) return;

      const validChunks = this.filterAndSortChunks(chunks);
      if (!validChunks) return;

      this.reconstructBufferFromChunks(validChunks);

      if (this.bufferSize > 0) {
        console.log("[ProxyUpload] Triggering upload of recovered chunks");
        await this.emergencyUpload();
      }
    } catch (error) {
      console.error("[ProxyUpload] Failed to recover from IndexedDB:", error);
    }
  }

  /**
   * Process and validate metadata from IndexedDB
   * @returns {Promise<boolean>} true if should continue recovery, false otherwise
   */
  async processMetadata(metadata) {
    if (!metadata) return true;

    console.log("[ProxyUpload] Found metadata in IndexedDB:", metadata);

    const maxAge = 30 * 60 * 1000; // 30 minutes
    const dataAge = Date.now() - metadata.lastUpdated;

    if (dataAge > maxAge) {
      console.log(
        "[ProxyUpload] Metadata too old, clearing stale data for audioFileId:",
        this.audioFileId,
      );
      await this.clearIndexedDB();
      return false;
    }

    if (metadata.status === "completed") {
      console.log("[ProxyUpload] Recording already completed, clearing data");
      await this.clearIndexedDB();
      return false;
    }

    this.uploadedBytes = metadata.uploadedBytes || 0;
    this.totalBytes = metadata.totalBytes || 0;
    this.chunkIndex = metadata.chunkIndex || 0;

    return true;
  }

  /**
   * Get all stored chunks for this audio file
   * @returns {Promise<Array>} Array of chunks
   */
  async getStoredChunks() {
    const tx = this.db.transaction("chunks", "readonly");
    const index = tx.objectStore("chunks").index("audioFileId");
    const chunks = await index.getAll(this.audioFileId);

    if (chunks.length > 0) {
      console.log(
        `[ProxyUpload] Found ${chunks.length} chunks in IndexedDB for audioFileId ${this.audioFileId}`,
      );
    }

    return chunks;
  }

  /**
   * Filter chunks by age and sort them
   * @returns {Array|null} Sorted valid chunks or null if none found
   */
  filterAndSortChunks(chunks) {
    const maxAge = 30 * 60 * 1000; // 30 minutes
    const now = Date.now();

    const validChunks = chunks.filter((chunk) => {
      const age = now - chunk.timestamp;
      return age <= maxAge;
    });

    if (validChunks.length !== chunks.length) {
      console.log(
        `[ProxyUpload] Filtered out ${
          chunks.length - validChunks.length
        } stale chunks`,
      );
    }

    if (validChunks.length === 0) {
      console.log("[ProxyUpload] No valid chunks found, clearing data");
      this.clearIndexedDB();
      return null;
    }

    // Sort by chunkSequence first, then timestamp to maintain order
    validChunks.sort((a, b) => {
      if (a.chunkSequence !== b.chunkSequence) {
        return a.chunkSequence - b.chunkSequence;
      }
      return a.timestamp - b.timestamp;
    });

    return validChunks;
  }

  /**
   * Reconstruct buffer from stored chunks
   */
  reconstructBufferFromChunks(validChunks) {
    for (const chunkData of validChunks) {
      const blob = new Blob([chunkData.data], {
        type: "audio/webm;codecs=opus",
      });
      this.buffer.push(blob);
      this.bufferSize += blob.size;
    }

    console.log(
      `[ProxyUpload] Recovered ${this.bufferSize} bytes from ${validChunks.length} chunks`,
    );
  }

  /**
   * Clean up stale data from IndexedDB (older than 1 hour)
   */
  async cleanupStaleData() {
    if (!this.db) return;

    try {
      const maxAge = 60 * 60 * 1000; // 1 hour
      const cutoffTime = Date.now() - maxAge;

      // Clean up old chunks
      const tx = this.db.transaction("chunks", "readwrite");
      const store = tx.objectStore("chunks");
      const index = store.index("timestamp");

      // Get all chunks older than cutoff
      const range = IDBKeyRange.upperBound(cutoffTime);
      const oldChunks = await index.getAll(range);

      for (const chunk of oldChunks) {
        await store.delete(chunk.id);
      }

      if (oldChunks.length > 0) {
        console.log(
          `[ProxyUpload] Cleaned up ${oldChunks.length} stale chunks from IndexedDB`,
        );
      }

      // Clean up old metadata
      const metaTx = this.db.transaction("metadata", "readwrite");
      const metaStore = metaTx.objectStore("metadata");
      const allMetadata = await metaStore.getAll();

      for (const meta of allMetadata) {
        if (meta.lastUpdated < cutoffTime) {
          await metaStore.delete(meta.audioFileId);
          console.log(
            `[ProxyUpload] Cleaned up stale metadata for audioFileId: ${meta.audioFileId}`,
          );
        }
      }
    } catch (error) {
      console.error("[ProxyUpload] Failed to cleanup stale data:", error);
    }
  }

  /**
   * Save chunk to IndexedDB
   */
  async saveChunkToIndexedDB(chunk) {
    if (!this.db) return;

    try {
      // Convert blob to ArrayBuffer for storage
      const arrayBuffer = await chunk.arrayBuffer();

      await this.db.add("chunks", {
        audioFileId: this.audioFileId,
        data: arrayBuffer,
        size: chunk.size,
        timestamp: Date.now(),
        sessionId:
          this.sessionId || `session-${this.audioFileId}-${Date.now()}`, // Track session
        chunkSequence: this.buffer.length, // Track chunk order within session
      });

      // Update metadata with session info
      await this.db.put("metadata", {
        audioFileId: this.audioFileId,
        uploadedBytes: this.uploadedBytes,
        totalBytes: this.totalBytes,
        chunkIndex: this.chunkIndex,
        sessionId:
          this.sessionId || `session-${this.audioFileId}-${Date.now()}`,
        lastUpdated: Date.now(),
        status: "recording", // Track recording status
      });
    } catch (error) {
      console.error("[ProxyUpload] Failed to save chunk to IndexedDB:", error);
    }
  }

  /**
   * Clear chunks from IndexedDB after successful upload
   */
  async clearIndexedDB() {
    if (!this.db) return;

    try {
      // Delete all chunks for this audio file
      const tx = this.db.transaction("chunks", "readwrite");
      const index = tx.objectStore("chunks").index("audioFileId");
      const keys = await index.getAllKeys(this.audioFileId);

      for (const key of keys) {
        await tx.objectStore("chunks").delete(key);
      }

      // Delete metadata
      await this.db.delete("metadata", this.audioFileId);

      console.log(
        "[ProxyUpload] Cleared IndexedDB for audioFileId:",
        this.audioFileId,
      );
    } catch (error) {
      console.error("[ProxyUpload] Failed to clear IndexedDB:", error);
    }
  }

  /**
   * Add a chunk to the upload queue
   * @param {Blob} chunk - The audio chunk to upload
   */
  async addChunk(chunk) {
    // Ensure DB is initialized
    await this.initDB();

    // Add to buffer instead of directly to chunks
    this.buffer.push(chunk);
    this.bufferSize += chunk.size;
    this.totalBytes += chunk.size;

    // Save chunk to IndexedDB for persistence
    await this.saveChunkToIndexedDB(chunk);

    // Create exact chunks when buffer is large enough
    while (this.bufferSize >= this.chunkSize) {
      this.createExactSizeChunk();
    }

    // If immediate upload is enabled, upload available chunks right away
    if (this.immediateUpload && this.chunks.length > 0) {
      await this.uploadPendingChunks();
    }
  }

  /**
   * Create a chunk of exactly chunkSize (256KB) from the buffer
   */
  createExactSizeChunk() {
    if (this.bufferSize < this.chunkSize) return;

    // Calculate how much data we need
    let remainingSize = this.chunkSize;
    const chunkParts = [];
    const newBuffer = [];
    let newBufferSize = 0;

    // Take data from buffer until we have exactly chunkSize
    for (const blob of this.buffer) {
      if (remainingSize === 0) {
        // We have enough, keep the rest in buffer
        newBuffer.push(blob);
        newBufferSize += blob.size;
      } else if (blob.size <= remainingSize) {
        // Use entire blob
        chunkParts.push(blob);
        remainingSize -= blob.size;
      } else {
        // Need to split this blob
        chunkParts.push(blob.slice(0, remainingSize));
        newBuffer.push(blob.slice(remainingSize));
        newBufferSize += blob.size - remainingSize;
        remainingSize = 0;
      }
    }

    // Create the exact-size chunk
    const exactChunk = new Blob(chunkParts, { type: "audio/webm;codecs=opus" });
    console.log(
      `[ProxyUpload] Created exact chunk of size: ${exactChunk.size} bytes (expected: ${this.chunkSize})`,
    );
    this.chunks.push(exactChunk);

    // Update buffer with remaining data
    this.buffer = newBuffer;
    this.bufferSize = newBufferSize;
  }

  /**
   * Force upload of remaining buffered data (for final chunk)
   * @param {boolean} isEmergencyFlush - If true, flush even small amounts for data preservation
   */
  flushBuffer(isEmergencyFlush = false) {
    if (this.buffer.length > 0 && this.bufferSize > 0) {
      // Check if this is a small recording that should be uploaded as single chunk
      if (this.totalBytes < this.chunkSize && this.uploadedBytes === 0) {
        console.log(
          `[ProxyUpload] Small recording detected during flush (${this.totalBytes} bytes), will upload as single chunk`,
        );
        // Don't create chunks for small recordings - handle in uploadAsingleChunk
        return;
      }

      // For the final chunk or emergency flush, we can send less than 256KB
      const finalChunk = new Blob(this.buffer, {
        type: "audio/webm;codecs=opus",
      });
      this.chunks.push(finalChunk);
      this.buffer = [];
      this.bufferSize = 0;
      // Mark that we've flushed the final chunk
      this.isFinalChunk = true;

      if (isEmergencyFlush) {
        console.log(
          "[ProxyUpload] Emergency flush: saving",
          finalChunk.size,
          "bytes",
        );
      }
    }
  }

  /**
   * Emergency upload for page refresh scenarios
   * Forces upload of any buffered data to prevent loss
   */
  async emergencyUpload() {
    console.log("[ProxyUpload] Emergency upload triggered");

    // Make sure DB is initialized before proceeding
    if (!this.db) {
      console.log("[ProxyUpload] DB not initialized, waiting...");
      await this.initDB();
    }

    console.log("[ProxyUpload] Current state:", {
      bufferLength: this.buffer.length,
      bufferSize: this.bufferSize,
      totalBytes: this.totalBytes,
      uploadedBytes: this.uploadedBytes,
      chunksLength: this.chunks.length,
    });

    // Log the emergency upload attempt with detailed state
    await criticalLoggingService.logEmergencyUpload({
      audioFileId: this.audioFileId,
      status: "started",
      trigger: "page_refresh",
      bufferLength: this.buffer.length,
      bufferSize: this.bufferSize,
      totalBytes: this.totalBytes,
      uploadedBytes: this.uploadedBytes,
      chunksLength: this.chunks.length,
    });

    // For small recordings, upload as single chunk instead of multiple chunks
    if (this.totalBytes > 0 && this.totalBytes < this.chunkSize) {
      console.log(
        `[ProxyUpload] Small recording detected (${this.totalBytes} bytes), uploading as single chunk`,
      );
      await this.uploadAsingleChunk();
    } else {
      // Flush any remaining buffer as final chunk
      this.flushBuffer(true);

      console.log("[ProxyUpload] After flush buffer:", {
        chunksLength: this.chunks.length,
        isFinalChunk: this.isFinalChunk,
        totalBytes: this.totalBytes,
      });

      // Upload all pending chunks
      if (this.chunks.length > 0) {
        await this.uploadPendingChunks();
      }
    }

    console.log(
      "[ProxyUpload] Emergency upload completed, uploaded:",
      this.uploadedBytes,
      "bytes",
    );

    // Log completion status
    await criticalLoggingService.logEmergencyUpload({
      audioFileId: this.audioFileId,
      status: "completed",
      trigger: "page_refresh",
      totalBytes: this.totalBytes,
      uploadedBytes: this.uploadedBytes,
    });
  }

  /**
   * Upload entire recording as a single chunk (for small recordings < 256KB)
   */
  async uploadAsingleChunk() {
    console.log("🔥 [ProxyUpload] *** SINGLE CHUNK UPLOAD METHOD CALLED ***");
    console.log(
      `🔥 [ProxyUpload] Buffer length: ${this.buffer.length}, Buffer size: ${this.bufferSize}`,
    );

    if (this.buffer.length === 0) {
      console.log("❌ [ProxyUpload] No buffer data to upload as single chunk");
      return;
    }

    // Combine all buffer data into one blob
    const singleChunk = new Blob(this.buffer, {
      type: "audio/webm;codecs=opus",
    });
    console.log(
      `🔥 [ProxyUpload] *** CREATED SINGLE CHUNK OF ${singleChunk.size} BYTES FROM ${this.buffer.length} BUFFER SEGMENTS ***`,
    );

    // Clear existing chunks and buffer
    this.chunks = [];
    this.buffer = [];
    this.bufferSize = 0;

    // Upload as single final chunk
    this.isFinalChunk = true;
    console.log(
      `🔥 [ProxyUpload] *** UPLOADING SINGLE CHUNK (${singleChunk.size} bytes) AS FINAL CHUNK ***`,
    );
    await this.uploadChunk(singleChunk, true);
  }

  /**
   * Upload all pending chunks
   * @returns {Promise<void>}
   */
  async uploadPendingChunks() {
    if (this.isUploading || this.chunks.length === 0) {
      return;
    }

    this.isUploading = true;

    try {
      while (this.chunks.length > 0) {
        const chunk = this.chunks.shift();
        // Check if this is the last chunk to upload
        const isLastChunk = this.chunks.length === 0 && this.isFinalChunk;
        await this.uploadChunk(chunk, isLastChunk);
      }
    } finally {
      this.isUploading = false;
    }
  }

  /**
   * Upload a single chunk through the backend proxy
   * @param {Blob} chunk - The chunk to upload
   * @param {boolean} isLastChunk - Whether this is the final chunk
   * @returns {Promise<void>}
   */
  async uploadChunk(chunk, isLastChunk = false) {
    console.log("[DEBUG] === uploadChunk called ===", {
      chunkIndex: this.chunkIndex,
      chunkSize: chunk.size,
    });

    // Note: We send what we think the bytes should be, but the backend will sync with GCS
    const startByte = this.uploadedBytes;
    const endByte = startByte + chunk.size - 1;

    console.log("[ProxyUpload] Uploading chunk:", {
      chunkSize: chunk.size,
      startByte,
      endByte,
      isLastChunk,
      uploadedBytes: this.uploadedBytes,
      totalBytes: this.totalBytes,
      chunkIndex: this.chunkIndex,
    });

    // Log chunk upload attempt with full details
    await criticalLoggingService.logChunkUpload("attempt", {
      audioFileId: this.audioFileId,
      chunkIndex: this.chunkIndex,
      chunkSize: chunk.size,
      startByte,
      endByte,
      isLastChunk,
      uploadedBytes: this.uploadedBytes,
      totalBytes: this.totalBytes,
      apiUrl: this.apiUrl,
    });

    try {
      // Get auth token from Auth0
      const token = await getAccessTokenSilentlyOutsideComponent();
      if (!token) {
        const error = new Error("No auth token available");

        // Log auth token failure
        await criticalLoggingService.logChunkUpload("auth_failed", {
          audioFileId: this.audioFileId,
          chunkIndex: this.chunkIndex,
          error: error.message,
        });

        throw error;
      }

      // For the final chunk, we need to send the exact total size
      // Calculate the actual total based on what we've uploaded plus this chunk
      const actualTotalSize = isLastChunk
        ? this.uploadedBytes + chunk.size
        : this.totalBytes;

      console.log(
        "[ProxyUpload] Calculated totalSize:",
        actualTotalSize,
        "(isLastChunk:",
        isLastChunk,
        ")",
      );

      const url =
        `${this.apiUrl}/recordings/${this.audioFileId}/chunks/${this.chunkIndex}?` +
        new URLSearchParams({
          startByte: startByte.toString(),
          endByte: endByte.toString(),
          totalSize: actualTotalSize.toString(),
          contentType: this.contentType,
        });

      // Log the actual request being made
      await criticalLoggingService.logChunkUpload("request_prepared", {
        audioFileId: this.audioFileId,
        chunkIndex: this.chunkIndex,
        url,
        method: "PUT",
        headers: {
          hasAuthToken: !!token,
          contentType: "application/octet-stream",
        },
        requestParams: {
          startByte,
          endByte,
          totalSize: actualTotalSize,
          contentType: "audio/webm;codecs=opus",
        },
        chunkSize: chunk.size,
      });

      console.log("[ProxyUpload] Making request to:", url);
      console.log("[ProxyUpload] Request headers:", {
        Authorization: `Bearer ${token.substring(0, 20)}...`,
        "Content-Type": "application/octet-stream",
      });
      console.log("[ProxyUpload] Request body size:", chunk.size);

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": this.contentType,
        },
        body: chunk,
      });

      console.log("[ProxyUpload] Response received:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      // Log the response details
      await criticalLoggingService.logChunkUpload("response_received", {
        audioFileId: this.audioFileId,
        chunkIndex: this.chunkIndex,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status}`;
        let errorDetails = null;

        try {
          errorDetails = await response.json();
          errorMessage = errorDetails.message || errorMessage;
        } catch (parseError) {
          // If response isn't JSON, try to get text
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            // If we can't parse anything, use the status
            errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
          }
        }

        // Log failed response details
        await criticalLoggingService.logChunkUpload("response_failed", {
          audioFileId: this.audioFileId,
          chunkIndex: this.chunkIndex,
          status: response.status,
          statusText: response.statusText,
          errorMessage,
          errorDetails,
        });

        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Log successful response details
      await criticalLoggingService.logChunkUpload("response_success", {
        audioFileId: this.audioFileId,
        chunkIndex: this.chunkIndex,
        status: response.status,
        result,
        serverUploadedBytes: result.uploadedBytes,
        currentUploadedBytes: this.uploadedBytes,
      });

      // Update our byte count based on what the server reports
      if (result.uploadedBytes) {
        this.uploadedBytes = result.uploadedBytes;
        console.log(
          `[ProxyUpload] Chunk uploaded, server reports ${this.uploadedBytes} total bytes uploaded`,
        );
      } else {
        // Fallback to our calculation if server doesn't report
        this.uploadedBytes += chunk.size;
      }

      this.chunkIndex++;

      console.log(
        `[ProxyUpload] Chunk ${this.chunkIndex - 1} uploaded successfully`,
      );

      // Log final success
      await criticalLoggingService.logChunkUpload("success", {
        audioFileId: this.audioFileId,
        chunkIndex: this.chunkIndex - 1,
        chunkSize: chunk.size,
        uploadedBytes: this.uploadedBytes,
        totalBytes: this.totalBytes,
      });

      // Mark as completed and clear from IndexedDB after successful upload
      if (this.uploadedBytes >= this.totalBytes && this.isFinalChunk) {
        // Update metadata to mark as completed before clearing
        await this.db.put("metadata", {
          audioFileId: this.audioFileId,
          uploadedBytes: this.uploadedBytes,
          totalBytes: this.totalBytes,
          chunkIndex: this.chunkIndex,
          sessionId: this.sessionId,
          lastUpdated: Date.now(),
          status: "completed",
        });

        // Then clear the data
        await this.clearIndexedDB();
      }
    } catch (error) {
      console.error("[ProxyUpload] Failed to upload chunk:", error);

      // Log detailed error information
      await criticalLoggingService.logChunkUpload("failed", {
        audioFileId: this.audioFileId,
        chunkIndex: this.chunkIndex,
        chunkSize: chunk.size,
        error: error.message,
        errorStack: error.stack,
        uploadedBytes: this.uploadedBytes,
        totalBytes: this.totalBytes,
        errorType: error.constructor.name,
      });

      // Re-add chunk to queue for retry, but don't increment chunkIndex
      this.chunks.unshift(chunk);
      throw error;
    }
  }

  /**
   * Get upload progress
   * @returns {number} Progress percentage (0-100)
   */
  getProgress() {
    if (this.totalBytes === 0) return 0;
    return Math.round((this.uploadedBytes / this.totalBytes) * 100);
  }

  /**
   * Cancel the upload
   */
  cancel() {
    this.chunks = [];
    this.isUploading = false;
  }

  /**
   * Set the Content-Type used to label chunk uploads. Should be the actual MIME
   * type chosen by the browser's MediaRecorder so the label matches the bytes.
   * @param {string} ct - e.g. "audio/mp4" (Safari) or "audio/webm;codecs=opus"
   */
  setContentType(ct) {
    this.contentType = ct || "audio/webm;codecs=opus";
  }

  /**
   * Check if service is ready
   * @returns {boolean}
   */
  isReady() {
    return true; // Always ready for proxy uploads
  }
}

export default ProxyUploadService;
